// Half-resolution ambient color + AO from voxel volume.
// Artistic approach: sample nearby voxels for local color tint + occlusion.

import type { GPU } from "./gpu.js";
import type { GBuffer } from "./deferred.js";

const GI_TRACE_SHADER = /* wgsl */ `
struct Params {
  invViewProj: mat4x4f,
  voxelMin: vec3f, _p0: f32,
  voxelMax: vec3f, _p1: f32,
  camPos: vec3f, _p2: f32,
  screenW: f32, screenH: f32, _p3: f32, _p4: f32,
};

@group(0) @binding(0) var normalTex: texture_2d<f32>;
@group(0) @binding(1) var depthTex: texture_depth_2d;
@group(0) @binding(2) var voxelVolume: texture_3d<f32>;
@group(0) @binding(3) var voxelSampler: sampler;
@group(0) @binding(4) var<uniform> params: Params;
@group(0) @binding(5) var outputTex: texture_storage_2d<rgba16float, write>;

fn reconstructWorldPos(coord: vec2i, depth: f32) -> vec3f {
  let screenSize = vec2f(params.screenW, params.screenH);
  let uv = (vec2f(coord) + 0.5) / screenSize;
  let ndc = vec4f(uv.x * 2.0 - 1.0, 1.0 - uv.y * 2.0, depth, 1.0);
  let world = params.invViewProj * ndc;
  return world.xyz / world.w;
}

fn worldToVoxelUV(wp: vec3f) -> vec3f {
  return (wp - params.voxelMin) / (params.voxelMax - params.voxelMin);
}

fn buildTBN(N: vec3f) -> mat3x3f {
  var T: vec3f;
  if (abs(N.y) < 0.999) { T = normalize(cross(vec3f(0.0, 1.0, 0.0), N)); }
  else { T = normalize(cross(vec3f(1.0, 0.0, 0.0), N)); }
  let B = cross(N, T);
  return mat3x3f(T, N, B);
}

// 6 hemisphere cone directions (tangent space) for local color bounce + corner AO
const CONE_DIRS = array<vec3f, 6>(
  vec3f(0.0, 1.0, 0.0),
  vec3f(0.866, 0.5, 0.0),
  vec3f(-0.866, 0.5, 0.0),
  vec3f(0.0, 0.5, 0.866),
  vec3f(0.0, 0.5, -0.866),
  vec3f(0.577, 0.577, 0.577),
);

const CONE_WEIGHTS = array<f32, 6>(
  0.25, 0.15, 0.15, 0.15, 0.15, 0.15,
);

// World-space sky visibility directions (normal-independent enclosure detection)
const SKY_DIRS = array<vec3f, 5>(
  vec3f(0.0, 1.0, 0.0),
  vec3f(0.5, 0.866, 0.0),
  vec3f(-0.5, 0.866, 0.0),
  vec3f(0.0, 0.866, 0.5),
  vec3f(0.0, 0.866, -0.5),
);

fn traceConeOcc(origin: vec3f, dir: vec3f, voxSize: f32, maxDist: f32, tanAng: f32) -> f32 {
  var occ = 0.0;
  var dist = voxSize * 3.0;
  for (var step = 0; step < 8; step++) {
    if (occ >= 0.95 || dist > maxDist) { break; }
    let pos = origin + dir * dist;
    let uv = worldToVoxelUV(pos);
    if (any(uv < vec3f(0.0)) || any(uv > vec3f(1.0))) { break; }
    let diameter = 2.0 * tanAng * dist;
    let mip = log2(max(1.0, diameter / voxSize));
    let s = textureSampleLevel(voxelVolume, voxelSampler, uv, mip);
    occ += s.a * (1.0 - occ);
    dist += max(diameter * 0.5, voxSize);
  }
  return occ;
}

@compute @workgroup_size(8, 8)
fn csGITrace(@builtin(global_invocation_id) id: vec3u) {
  let outSize = textureDimensions(outputTex);
  if (id.x >= outSize.x || id.y >= outSize.y) { return; }

  let fullCoord = vec2i(id.xy) * 2;
  let normalSample = textureLoad(normalTex, fullCoord, 0);
  let normal = normalSample.rgb;
  if (dot(normal, normal) < 0.01) {
    textureStore(outputTex, vec2i(id.xy), vec4f(0.0));
    return;
  }

  let depth = textureLoad(depthTex, fullCoord, 0);
  let worldPos = reconstructWorldPos(fullCoord, depth);
  let N = normalize(normal);
  let tbn = buildTBN(N);

  let voxelExtent = params.voxelMax - params.voxelMin;
  let voxSize = max(voxelExtent.x, max(voxelExtent.y, voxelExtent.z)) / 256.0;
  let sampleOrigin = worldPos + N * voxSize * 1.5;
  let maxDist = length(voxelExtent) * 0.4;
  let tanAngle = 0.577; // ~30° cone half-angle

  var colorAccum = vec3f(0.0);
  var localAO = 0.0;

  // Phase 1: hemisphere cones — local color bounce + corner AO
  for (var i = 0; i < 6; i++) {
    let dir = normalize(tbn * CONE_DIRS[i]);

    var coneColor = vec3f(0.0);
    var coneOcc = 0.0;
    var dist = voxSize * 3.0;

    for (var step = 0; step < 8; step++) {
      if (coneOcc >= 0.95 || dist > maxDist) { break; }

      let samplePos = sampleOrigin + dir * dist;
      let uv = worldToVoxelUV(samplePos);
      if (any(uv < vec3f(0.0)) || any(uv > vec3f(1.0))) { break; }

      let diameter = 2.0 * tanAngle * dist;
      let mip = log2(max(1.0, diameter / voxSize));
      let s = textureSampleLevel(voxelVolume, voxelSampler, uv, mip);

      let a = s.a * (1.0 - coneOcc);
      coneColor += s.rgb * a;
      coneOcc += a;

      dist += max(diameter * 0.5, voxSize);
    }

    let w = CONE_WEIGHTS[i];
    colorAccum += coneColor * w;
    localAO += coneOcc * w;
  }

  // Phase 2: sky visibility — world-space upward cones (normal-independent)
  // Detects enclosure: a ceiling blocks sky, darkening the entire room uniformly
  // Start from normal-offset origin so edge pixels don't slip through voxel seams
  var skyBlock = 0.0;
  for (var s = 0; s < 5; s++) {
    skyBlock += traceConeOcc(sampleOrigin, SKY_DIRS[s], voxSize, maxDist, tanAngle);
  }
  skyBlock /= 5.0;

  // Combine: local AO for corners + sky occlusion for enclosed volume darkening
  let ao = max(sqrt(localAO) * 0.39, skyBlock * 0.52);
  textureStore(outputTex, vec2i(id.xy), vec4f(colorAccum, ao));
}
`;

export class GITrace {
  output: GPUTexture;
  outputView: GPUTextureView;
  private pipeline: GPUComputePipeline;
  private bindGroup!: GPUBindGroup;
  private paramsBuffer: GPUBuffer;
  private halfW: number;
  private halfH: number;

  constructor(private gpu: GPU, gBuffer: GBuffer, depthTexture: GPUTexture, voxelVolume: GPUTexture, voxelSampler: GPUSampler) {
    const { device, canvas } = gpu;
    this.halfW = Math.ceil(canvas.width / 2);
    this.halfH = Math.ceil(canvas.height / 2);

    this.output = device.createTexture({
      size: [this.halfW, this.halfH],
      format: "rgba16float",
      usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.outputView = this.output.createView();

    const module = device.createShaderModule({ code: GI_TRACE_SHADER });
    const layout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "depth" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float", viewDimension: "3d" } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, sampler: {} },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
        { binding: 5, visibility: GPUShaderStage.COMPUTE, storageTexture: { format: "rgba16float", access: "write-only" } },
      ],
    });
    this.pipeline = device.createComputePipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [layout] }),
      compute: { module, entryPoint: "csGITrace" },
    });

    // 24 floats = 96 bytes (round to 128 for alignment)
    this.paramsBuffer = device.createBuffer({ size: 128, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    this.bindGroup = device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: gBuffer.normal.createView() },
        { binding: 1, resource: depthTexture.createView() },
        { binding: 2, resource: voxelVolume.createView({ dimension: "3d" }) },
        { binding: 3, resource: voxelSampler },
        { binding: 4, resource: { buffer: this.paramsBuffer } },
        { binding: 5, resource: this.outputView },
      ],
    });
  }

  compute(encoder: GPUCommandEncoder, invViewProj: Float32Array, voxelMin: [number,number,number], voxelMax: [number,number,number], camPos: [number,number,number]): void {
    const data = new Float32Array(32);
    data.set(invViewProj, 0);
    data[16] = voxelMin[0]; data[17] = voxelMin[1]; data[18] = voxelMin[2];
    data[20] = voxelMax[0]; data[21] = voxelMax[1]; data[22] = voxelMax[2];
    data[24] = camPos[0]; data[25] = camPos[1]; data[26] = camPos[2];
    data[28] = this.gpu.canvas.width; data[29] = this.gpu.canvas.height;
    this.gpu.device.queue.writeBuffer(this.paramsBuffer, 0, data);

    const pass = encoder.beginComputePass();
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.dispatchWorkgroups(Math.ceil(this.halfW / 8), Math.ceil(this.halfH / 8));
    pass.end();
  }
}
